"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Topbar } from "./_components/Topbar";
import { useToast } from "./_components/Toast";
import {
  AlertIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  InfoIcon,
  LockIcon,
  MailIcon,
} from "./_components/icons";
import {
  EMAIL_RE,
  RESEND_COOLDOWN_S,
  DEMO_OTP,
  requestOtp,
  startCandidateSession,
  verifyOtp,
} from "./_mock/journey";
import {
  persistInviteSession,
  requestInviteOtp,
  resendInviteOtp,
  resolveInvite,
  verifyInviteOtp,
} from "@/lib/invite";
import { DEMO_MODE } from "@/lib/demoMode";
import { identifyPostHogUser } from "@/lib/posthog";
import { forgetReturnTo } from "@/lib/studentSession";
import {
  trackInviteLinkInvalid,
  trackInviteLinkOpened,
  trackInviteOtpRequestFailed,
  trackInviteOtpRequested,
  trackInviteOtpVerificationFailed,
  trackInviteOtpVerified,
} from "@/lib/assessmentEvents";

/**
 * Step 1 — candidate sign-in: email → OTP.
 *
 * Markup and CSS classes are ported 1:1 from the candidate-assessment-journey
 * prototype (#stage-auth / #stage-otp), which in turn matches ai-interview's
 * email/OTP stages. Everything visual comes from ds/base.css (.auth-icon,
 * .field, .input, .err-msg, .otp-boxes, .demo-hint, .resend-row, .btn).
 * The invite token is minted by the short-link resolver. All OTP calls go
 * through same-origin BFF routes so service URLs never reach the browser.
 */
const OTP_LENGTH = 6;

export default function SignInPage() {
  const router = useRouter();
  const { toast, toastNode } = useToast();

  const inviteToken = useRef("");
  // DESIGN-ONLY: previews the corporate completion copy on the demo (no
  // invite-token) sign-in path — see journey.ts's startCandidateSession.
  const demoEntity = useRef<"corporate" | "institute">("institute");
  const [invite, setInvite] = useState<Record<string, unknown>>({});
  const [resolving, setResolving] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [stage, setStage] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [sending, setSending] = useState(false);

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [otpErr, setOtpErr] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const [sentAt, setSentAt] = useState(0);

  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const code = digits.join("");

  /* What the resolved invite says about this sitting. Every invite/OTP event
     carries these three: the session that would otherwise supply them does not
     exist until the OTP verifies, and on a shared browser the only thing
     sessionStorage could offer before then is the previous candidate's. */
  const assessmentType = invite.assessmentType ? String(invite.assessmentType) : null;
  const role = invite.role ? String(invite.role) : null;
  const company = invite.company ? String(invite.company) : null;

  const resetOtp = useCallback(() => {
    setDigits(Array(OTP_LENGTH).fill(""));
    setOtpErr("");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("inviteToken") || "";
    if (params.get("entity") === "corporate") demoEntity.current = "corporate";
    if (!token) {
      // The public root remains the local/demo candidate entry point, but only
      // where the demo journey is switched on — everywhere else a URL without an
      // invite token has no assessment to open and must not offer a sign-in.
      // Only a URL that actually carries a token uses the invite resolver.
      let active = true;
      void Promise.resolve().then(() => {
        if (!active) return;
        if (!DEMO_MODE) {
          setFatalError("Open your assessment from the invite link we emailed you.");
        }
        setResolving(false);
      });
      return () => { active = false; };
    }
    inviteToken.current = token;
    let active = true;
    void resolveInvite(token)
      .then((data) => {
        if (!active) return;
        const status = String(data.status || "").toUpperCase();
        const type = data.assessmentType ? String(data.assessmentType) : null;
        const openedRole = data.role ? String(data.role) : null;
        const openedCompany = data.company ? String(data.company) : null;
        if (["COMPLETED", "INPROGRESS", "DROPOUT"].includes(status) || data.submitted === true || data.attempted === true) {
          /* A resolvable link that has already been used is a different thing
             from a broken one — it is a candidate coming back, and the funnel
             has to be able to tell them apart. */
          trackInviteLinkOpened(type, "already_attempted", openedRole, openedCompany);
          setFatalError("This assessment has already been started or completed.");
          return;
        }
        trackInviteLinkOpened(type, "new", openedRole, openedCompany);
        setInvite(data);
      })
      .catch((error: unknown) => {
        const message = error instanceof Error ? error.message : "This invite link is invalid or expired.";
        trackInviteLinkInvalid(message);
        if (active) setFatalError(message);
      })
      .finally(() => {
        if (active) setResolving(false);
      });
    return () => { active = false; };
  }, []);

  /* ---------------------------------------------------------- step 1 ---- */
  async function onSendOtp(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setEmailErr("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const res = inviteToken.current
        ? await requestInviteOtp(inviteToken.current, value)
        : await requestOtp(value);
      setEmail(value);
      resetOtp();
      setSentAt("sentAt" in res ? Number(res.sentAt) : 0);
      setCooldown("resend_after" in res ? Number(res.resend_after) || RESEND_COOLDOWN_S : RESEND_COOLDOWN_S);
      setStage("otp");
      trackInviteOtpRequested(assessmentType, false, role, company);
      toast(`OTP sent to ${value}`);
    } catch (error) {
      trackInviteOtpRequestFailed(
        assessmentType,
        false,
        error instanceof Error ? error.message : "Unknown error",
      );
      setEmailErr("We couldn't send a code just now. Try again in a moment.");
    } finally {
      setSending(false);
    }
  }

  /* ---------------------------------------------------------- step 2 ---- */
  // Resend cooldown. One interval per tick, cleared when it lands on 0.
  useEffect(() => {
    if (stage !== "otp" || cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((n) => n - 1), 1000);
    return () => window.clearInterval(id);
  }, [stage, cooldown]);

  useEffect(() => {
    if (stage === "otp") boxes.current[0]?.focus();
  }, [stage]);

  function setDigit(i: number, value: string) {
    const d = value.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => prev.map((c, idx) => (idx === i ? d : c)));
    setOtpErr("");
    if (d && i < OTP_LENGTH - 1) boxes.current[i + 1]?.focus();
  }

  function onOtpKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      boxes.current[i - 1]?.focus();
    }
    if (e.key === "Enter") {
      e.preventDefault();
      void onVerify();
    }
  }

  function onOtpPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    setDigits((prev) => prev.map((c, i) => pasted[i] ?? c));
    setOtpErr("");
    boxes.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  async function onResend() {
    if (cooldown > 0) return;
    try {
      const res = inviteToken.current
        ? await resendInviteOtp(inviteToken.current, email)
        : await requestOtp(email);
      resetOtp();
      setSentAt("sentAt" in res ? Number(res.sentAt) : 0);
      setCooldown("resend_after" in res ? Number(res.resend_after) || RESEND_COOLDOWN_S : RESEND_COOLDOWN_S);
      boxes.current[0]?.focus();
      trackInviteOtpRequested(assessmentType, true, role, company);
      toast("New OTP sent");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not resend the code.";
      trackInviteOtpRequestFailed(assessmentType, true, message);
      setOtpErr(message);
    }
  }

  async function onVerify() {
    if (code.length < OTP_LENGTH || verifying) return;
    setVerifying(true);
    try {
      if (inviteToken.current) {
        const data = await verifyInviteOtp(inviteToken.current, email, code);
        /* An OTP invite is not the signed-in institute-dashboard handoff.
           Drop any marker a reused tab may carry from an earlier sitting. */
        forgetReturnTo();
        persistInviteSession(data, invite, email);
        const verifiedEmail = String(data.email || email);
        // An invite-token sitting is always corporate — institute never
        // issues one (README: institute students sign in, they aren't invited).
        startCandidateSession(verifiedEmail, "corporate");
        /* The verified email is the person. Identifying here also
           back-attributes this session's anonymous invite_link_opened and
           invite_otp_* events to them. */
        identifyPostHogUser(verifiedEmail, {
          email: verifiedEmail,
          studentId: invite.studentId,
          loginMethod: "otp_invite",
          entityName: company,
          role,
        });
        trackInviteOtpVerified(
          data.assessment_assigned_id ? String(data.assessment_assigned_id) : null,
          assessmentType,
          role,
          company,
        );
      } else {
        const failure = await verifyOtp(code, sentAt);
        if (failure === "expired") throw new Error("This code has expired. Request a new one.");
        if (failure === "wrong") throw new Error("That code isn't right. Check your 6 digits.");
        startCandidateSession(email, demoEntity.current);
      }
      router.push("/assessment");
    } catch (error) {
      const message = error instanceof Error ? error.message : "That code isn't right. Check your 6 digits.";
      trackInviteOtpVerificationFailed(assessmentType, message);
      setOtpErr(message);
      toast(message, "error");
    } finally {
      setVerifying(false);
    }
  }

  /* ----------------------------------------------------------- render ---- */
  return (
    <>
      <Topbar />
      <main>
        {resolving || fatalError ? (
          <section className="stage active">
            <div className="wrap narrow auth">
              <div className="card card-pad" aria-busy={resolving}>
                <div className="auth-icon"><LockIcon /></div>
                <h1>{resolving ? "Opening your assessment" : "Invite unavailable"}</h1>
                <p className="sub">{resolving ? "Securely checking your invite…" : fatalError}</p>
              </div>
            </div>
          </section>
        ) : stage === "email" ? (
          <section className="stage active">
            <div className="wrap narrow auth">
              <div className="card card-pad">
                <div className="auth-icon">
                  <MailIcon />
                </div>
                <h1>Verify your email</h1>
                <p className="sub">
                  Enter your email to get a 6-digit code and start your
                  assessment.
                </p>
                <form onSubmit={onSendOtp} noValidate>
                  <div className="field">
                    <label htmlFor="emailInput">Email address</label>
                    <input
                      className={`input${emailErr ? " err" : ""}`}
                      id="emailInput"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      aria-invalid={!!emailErr}
                      aria-describedby={emailErr ? "emailErr" : undefined}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailErr("");
                      }}
                    />
                    {emailErr ? (
                      <div className="err-msg" id="emailErr" role="alert">
                        <AlertIcon />
                        {emailErr}
                      </div>
                    ) : null}
                  </div>
                  <button
                    className="btn btn-primary btn-lg btn-block"
                    style={{ marginTop: 18 }}
                    type="submit"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <span className="spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send OTP
                        <ArrowRightIcon />
                      </>
                    )}
                  </button>
                </form>
                <p className="foot">
                  Trouble signing in?{" "}
                  <a href="mailto:support@pluginlive.com">
                    Contact your recruiter
                  </a>
                </p>
              </div>
            </div>
          </section>
        ) : (
          <section className="stage active">
            <div className="wrap narrow auth">
              <div className="card card-pad">
                <button
                  type="button"
                  className="back-link"
                  onClick={() => {
                    setStage("email");
                    resetOtp();
                  }}
                >
                  <ArrowLeftIcon />
                  Use a different email
                </button>
                <div className="auth-icon">
                  <LockIcon />
                </div>
                <h1>Enter the 6-digit code</h1>
                <p className="sub">
                  Code sent to <b>{email}</b>. Valid for a few minutes.
                </p>
                <div
                  className={`otp-boxes${otpErr ? " err" : ""}`}
                  role="group"
                  aria-label="6-digit verification code"
                >
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        boxes.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      autoComplete="one-time-code"
                      aria-label={`Digit ${i + 1}`}
                      aria-invalid={!!otpErr}
                      value={d}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => onOtpKeyDown(i, e)}
                      onPaste={onOtpPaste}
                    />
                  ))}
                </div>
                {otpErr ? (
                  <div className="err-msg" role="alert">
                    <AlertIcon />
                    {otpErr}
                  </div>
                ) : null}
                {DEMO_MODE && Object.keys(invite).length === 0 ? <div className="demo-hint"><InfoIcon />Demo code <b>{DEMO_OTP}</b></div> : null}
                <div className="resend-row">
                  <span>Didn&apos;t get the code?</span>
                  <button
                    type="button"
                    onClick={onResend}
                    disabled={cooldown > 0}
                  >
                    {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
                  </button>
                </div>
                <button
                  className="btn btn-primary btn-lg btn-block"
                  style={{ marginTop: 18 }}
                  type="button"
                  onClick={onVerify}
                  disabled={code.length < OTP_LENGTH || verifying}
                >
                  {verifying ? (
                    <>
                      <span className="spin" />
                      Verifying…
                    </>
                  ) : (
                    <>
                      Verify &amp; Continue
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>
        )}
      </main>
      {toastNode}
    </>
  );
}
